import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import { getApiBaseUrl } from '../config/api';

type TabType = 'overview' | 'pending' | 'active';

interface PaginationMeta {
  total: number;
  limit: number;
  page: number;
  pages: number;
}

interface ExclusiveCreatorApplication {
  _id: string;
  UserID: string;
  Status: string;
  ContentCategory?: string;
  ContentDescription?: string;
  createdAt: string;
  updatedAt: string;
}

interface ApplicationsResponse {
  message: string;
  data: ExclusiveCreatorApplication[];
  pagination?: PaginationMeta;
}

interface FetchApplicationsParams {
  status?: string;
  limit?: number;
  page?: number;
  startDate?: string;
  endDate?: string;
}

const defaultPagination: PaginationMeta = {
  total: 0,
  limit: 10,
  page: 1,
  pages: 1,
};

const formatDate = (dateString?: string) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusBadgeClass = (status: string) => {
  if (status === 'PENDING') return 'bg-amber-100 text-amber-700 border-amber-200';
  if (status === 'APPROVED') return 'bg-emerald-100 text-emerald-700 border-emerald-200';
  if (status === 'REJECTED') return 'bg-rose-100 text-rose-700 border-rose-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    throw new Error('No token found. Please log in.');
  }

  return {
    'Content-Type': 'application/json',
    Authorization: token,
  };
};

const ExclusiveMembers: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [activeLoading, setActiveLoading] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState<string>('');

  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  });

  const [recentApplications, setRecentApplications] = useState<ExclusiveCreatorApplication[]>([]);
  const [pendingApplications, setPendingApplications] = useState<ExclusiveCreatorApplication[]>([]);
  const [activeCreators, setActiveCreators] = useState<ExclusiveCreatorApplication[]>([]);
  const [pendingPagination, setPendingPagination] = useState<PaginationMeta>(defaultPagination);
  const [activePagination, setActivePagination] = useState<PaginationMeta>(defaultPagination);

  const fetchApplications = useCallback(async (params: FetchApplicationsParams = {}): Promise<ApplicationsResponse> => {
    const query = new URLSearchParams();

    if (params.status) query.set('status', params.status);
    if (params.limit) query.set('limit', String(params.limit));
    if (params.page) query.set('page', String(params.page));
    if (params.startDate) query.set('startDate', params.startDate);
    if (params.endDate) query.set('endDate', params.endDate);

    const queryString = query.toString();
    const endpoint = `${getApiBaseUrl()}/user/t/getExclusiveCreatorApplications${queryString ? `?${queryString}` : ''}`;

    const res = await fetch(endpoint, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json?.message || 'Failed to fetch exclusive creator applications');
    }

    return {
      message: json?.message || '',
      data: Array.isArray(json?.data) ? (json.data as ExclusiveCreatorApplication[]) : [],
      pagination: json?.pagination,
    };
  }, []);

  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const [allRes, pendingRes, approvedRes, rejectedRes] = await Promise.all([
        fetchApplications({ limit: 5, page: 1 }),
        fetchApplications({ status: 'PENDING', limit: 1, page: 1 }),
        fetchApplications({ status: 'APPROVED', limit: 1, page: 1 }),
        fetchApplications({ status: 'REJECTED', limit: 1, page: 1 }),
      ]);

      const allTotal = allRes.pagination?.total ?? allRes.data.length;
      const pendingTotal = pendingRes.pagination?.total ?? pendingRes.data.length;
      const approvedTotal = approvedRes.pagination?.total ?? approvedRes.data.length;
      const rejectedTotal = rejectedRes.pagination?.total ?? rejectedRes.data.length;

      setStats({
        total: allTotal,
        pending: pendingTotal,
        approved: approvedTotal,
        rejected: rejectedTotal,
      });
      setRecentApplications(allRes.data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load overview');
    } finally {
      setOverviewLoading(false);
    }
  }, [fetchApplications]);

  const fetchPending = useCallback(async (page: number = 1) => {
    setPendingLoading(true);
    try {
      const response = await fetchApplications({
        status: 'PENDING',
        limit: 10,
        page,
      });

      setPendingApplications(response.data);
      setPendingPagination(response.pagination || defaultPagination);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load pending approvals');
    } finally {
      setPendingLoading(false);
    }
  }, [fetchApplications]);

  const fetchActiveCreators = useCallback(async (page: number = 1) => {
    setActiveLoading(true);
    try {
      const response = await fetchApplications({
        status: 'APPROVED',
        limit: 10,
        page,
      });

      setActiveCreators(response.data);
      setActivePagination(response.pagination || defaultPagination);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load active creators');
    } finally {
      setActiveLoading(false);
    }
  }, [fetchApplications]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    if (activeTab === 'pending') {
      fetchPending(1);
    }

    if (activeTab === 'active') {
      fetchActiveCreators(1);
    }
  }, [activeTab, fetchActiveCreators, fetchPending]);

  const handleApprovalDecision = async (
    userId: string,
    decision: 'approved' | 'rejected',
  ) => {
    let rejectionReason = '';
    if (decision === 'rejected') {
      const input = window.prompt('Enter rejection reason (optional):', '');
      if (input === null) return;
      rejectionReason = input.trim();
    }

    setDecisionLoading(`${userId}:${decision}`);

    try {
      const res = await fetch(`${getApiBaseUrl()}/user/t/approveExclusiveCreator`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          UserID: userId,
          ApprovalStatus: decision,
          ...(rejectionReason ? { RejectionReason: rejectionReason } : {}),
        }),
      });

      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.message || 'Failed to update application');
      }

      toast.success(json?.message || 'Application updated');

      await Promise.all([
        fetchOverview(),
        fetchPending(pendingPagination.page || 1),
        fetchActiveCreators(activePagination.page || 1),
      ]);
    } catch (error: any) {
      toast.error(error.message || 'Failed to process application');
    } finally {
      setDecisionLoading('');
    }
  };

  return (
    <div className='min-h-screen bg-gray-50'>
      <Navbar />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <div className='mb-8'>
          <h1 className='text-3xl font-bold text-gray-900'>Exclusive Members</h1>
          <p className='text-gray-600 mt-2'>
            Review exclusive creator applications, approve pending requests, and monitor active creators.
          </p>
        </div>

        <div className='mb-6 flex flex-wrap gap-3'>
          {([
            { id: 'overview', label: 'Overall Statistics' },
            { id: 'pending', label: 'Waiting Approval' },
            { id: 'active', label: 'Active Creator' },
          ] as { id: TabType; label: string }[]).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2.5 rounded-lg font-semibold transition-all ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white shadow-md'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div className='space-y-6'>
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
              <div className='rounded-xl bg-white border border-gray-200 p-5 shadow-sm'>
                <p className='text-sm text-gray-500'>Total Applications</p>
                <p className='mt-1 text-3xl font-bold text-gray-900'>{overviewLoading ? '...' : stats.total}</p>
              </div>
              <div className='rounded-xl bg-white border border-gray-200 p-5 shadow-sm'>
                <p className='text-sm text-gray-500'>Pending</p>
                <p className='mt-1 text-3xl font-bold text-amber-600'>{overviewLoading ? '...' : stats.pending}</p>
              </div>
              <div className='rounded-xl bg-white border border-gray-200 p-5 shadow-sm'>
                <p className='text-sm text-gray-500'>Approved</p>
                <p className='mt-1 text-3xl font-bold text-emerald-600'>{overviewLoading ? '...' : stats.approved}</p>
              </div>
              <div className='rounded-xl bg-white border border-gray-200 p-5 shadow-sm'>
                <p className='text-sm text-gray-500'>Rejected</p>
                <p className='mt-1 text-3xl font-bold text-rose-600'>{overviewLoading ? '...' : stats.rejected}</p>
              </div>
            </div>

            <div className='bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden'>
              <div className='px-5 py-4 border-b border-gray-200 flex items-center justify-between'>
                <h2 className='text-lg font-semibold text-gray-900'>Recent Applications</h2>
                <button
                  onClick={fetchOverview}
                  className='text-sm px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50'
                >
                  Refresh
                </button>
              </div>
              <div className='overflow-x-auto'>
                <table className='min-w-full divide-y divide-gray-200'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider'>User ID</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider'>Category</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider'>Status</th>
                      <th className='px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider'>Submitted At</th>
                    </tr>
                  </thead>
                  <tbody className='bg-white divide-y divide-gray-100'>
                    {recentApplications.length === 0 && !overviewLoading && (
                      <tr>
                        <td className='px-5 py-6 text-sm text-gray-500' colSpan={4}>
                          No applications found.
                        </td>
                      </tr>
                    )}
                    {overviewLoading && (
                      <tr>
                        <td className='px-5 py-6 text-sm text-gray-500' colSpan={4}>
                          Loading overview...
                        </td>
                      </tr>
                    )}
                    {recentApplications.map((application) => (
                      <tr key={application._id}>
                        <td className='px-5 py-4 text-sm font-medium text-gray-900'>{application.UserID}</td>
                        <td className='px-5 py-4 text-sm text-gray-700'>{application.ContentCategory || 'N/A'}</td>
                        <td className='px-5 py-4 text-sm'>
                          <span className={`inline-flex px-2.5 py-1 rounded-full border text-xs font-semibold ${getStatusBadgeClass(application.Status)}`}>
                            {application.Status}
                          </span>
                        </td>
                        <td className='px-5 py-4 text-sm text-gray-700'>{formatDate(application.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'pending' && (
          <div className='bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden'>
            <div className='px-5 py-4 border-b border-gray-200 flex items-center justify-between'>
              <h2 className='text-lg font-semibold text-gray-900'>Pending Approvals</h2>
              <button
                onClick={() => fetchPending(pendingPagination.page || 1)}
                className='text-sm px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50'
              >
                Refresh
              </button>
            </div>

            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-gray-200'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider'>User ID</th>
                    <th className='px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider'>Category</th>
                    <th className='px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider'>Description</th>
                    <th className='px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider'>Submitted At</th>
                    <th className='px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider'>Actions</th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-100'>
                  {pendingLoading && (
                    <tr>
                      <td className='px-5 py-6 text-sm text-gray-500' colSpan={5}>
                        Loading pending applications...
                      </td>
                    </tr>
                  )}

                  {!pendingLoading && pendingApplications.length === 0 && (
                    <tr>
                      <td className='px-5 py-6 text-sm text-gray-500' colSpan={5}>
                        No pending applications.
                      </td>
                    </tr>
                  )}

                  {!pendingLoading && pendingApplications.map((application) => {
                    const approveId = `${application.UserID}:approved`;
                    const rejectId = `${application.UserID}:rejected`;
                    const isApproveLoading = decisionLoading === approveId;
                    const isRejectLoading = decisionLoading === rejectId;

                    return (
                      <tr key={application._id}>
                        <td className='px-5 py-4 text-sm font-medium text-gray-900'>{application.UserID}</td>
                        <td className='px-5 py-4 text-sm text-gray-700'>{application.ContentCategory || 'N/A'}</td>
                        <td className='px-5 py-4 text-sm text-gray-700 max-w-md'>
                          <p className='truncate' title={application.ContentDescription || ''}>
                            {application.ContentDescription || 'N/A'}
                          </p>
                        </td>
                        <td className='px-5 py-4 text-sm text-gray-700'>{formatDate(application.createdAt)}</td>
                        <td className='px-5 py-4 text-sm'>
                          <div className='flex items-center gap-2'>
                            <button
                              onClick={() => handleApprovalDecision(application.UserID, 'approved')}
                              disabled={Boolean(decisionLoading)}
                              className='px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed'
                            >
                              {isApproveLoading ? 'Approving...' : 'Approve'}
                            </button>
                            <button
                              onClick={() => handleApprovalDecision(application.UserID, 'rejected')}
                              disabled={Boolean(decisionLoading)}
                              className='px-3 py-1.5 rounded-md bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700 disabled:opacity-60 disabled:cursor-not-allowed'
                            >
                              {isRejectLoading ? 'Rejecting...' : 'Reject'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className='px-5 py-4 border-t border-gray-200 flex items-center justify-between'>
              <p className='text-sm text-gray-600'>
                Page {pendingPagination.page} of {Math.max(pendingPagination.pages, 1)} | Total {pendingPagination.total}
              </p>
              <div className='flex items-center gap-2'>
                <button
                  onClick={() => fetchPending(Math.max(1, pendingPagination.page - 1))}
                  disabled={pendingPagination.page <= 1 || pendingLoading}
                  className='px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchPending(Math.min(Math.max(pendingPagination.pages, 1), pendingPagination.page + 1))}
                  disabled={pendingPagination.page >= Math.max(pendingPagination.pages, 1) || pendingLoading}
                  className='px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'active' && (
          <div className='bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden'>
            <div className='px-5 py-4 border-b border-gray-200 flex items-center justify-between'>
              <h2 className='text-lg font-semibold text-gray-900'>Active Creators</h2>
              <button
                onClick={() => fetchActiveCreators(activePagination.page || 1)}
                className='text-sm px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50'
              >
                Refresh
              </button>
            </div>

            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-gray-200'>
                <thead className='bg-gray-50'>
                  <tr>
                    <th className='px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider'>User ID</th>
                    <th className='px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider'>Category</th>
                    <th className='px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider'>Status</th>
                    <th className='px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider'>Approved At</th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-gray-100'>
                  {activeLoading && (
                    <tr>
                      <td className='px-5 py-6 text-sm text-gray-500' colSpan={4}>
                        Loading active creators...
                      </td>
                    </tr>
                  )}

                  {!activeLoading && activeCreators.length === 0 && (
                    <tr>
                      <td className='px-5 py-6 text-sm text-gray-500' colSpan={4}>
                        No active creators yet.
                      </td>
                    </tr>
                  )}

                  {!activeLoading && activeCreators.map((application) => (
                    <tr key={application._id}>
                      <td className='px-5 py-4 text-sm font-medium text-gray-900'>{application.UserID}</td>
                      <td className='px-5 py-4 text-sm text-gray-700'>{application.ContentCategory || 'N/A'}</td>
                      <td className='px-5 py-4 text-sm'>
                        <span className={`inline-flex px-2.5 py-1 rounded-full border text-xs font-semibold ${getStatusBadgeClass(application.Status)}`}>
                          {application.Status}
                        </span>
                      </td>
                      <td className='px-5 py-4 text-sm text-gray-700'>{formatDate(application.updatedAt || application.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className='px-5 py-4 border-t border-gray-200 flex items-center justify-between'>
              <p className='text-sm text-gray-600'>
                Page {activePagination.page} of {Math.max(activePagination.pages, 1)} | Total {activePagination.total}
              </p>
              <div className='flex items-center gap-2'>
                <button
                  onClick={() => fetchActiveCreators(Math.max(1, activePagination.page - 1))}
                  disabled={activePagination.page <= 1 || activeLoading}
                  className='px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchActiveCreators(Math.min(Math.max(activePagination.pages, 1), activePagination.page + 1))}
                  disabled={activePagination.page >= Math.max(activePagination.pages, 1) || activeLoading}
                  className='px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExclusiveMembers;
